import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #cartDrawerMorphObserver = null;

  /** @type {number} */
  #cartDrawerRecSwiperRaf = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #cartDrawerRecSwiperTimeout = null;

  /** @type {boolean} */
  #swiperRefreshQueued = false;

  /** @type {boolean} */
  #ignoreMorphObserver = false;

  /** @type {number} */
  #pendingSwiperRetries = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleSwiperRefreshAfterCartMorph);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#initSwipersInDrawer);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#ensureDrawerMorphObserver();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#openFromUrlIfRequested();
  }

  #openFromUrlIfRequested = () => {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }

    const keys = ['show_cart_drawer', 'open_cart'];
    const key = keys.find((k) => params.has(k));
    if (!key) return;

    const v = (params.get(key) ?? '').toLowerCase();
    if (v === '0' || v === 'false' || v === 'no') return;

    this.open();
    for (const k of keys) params.delete(k);

    const qs = params.toString();
    const { pathname, hash } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${qs ? `?${qs}` : ''}${hash}`);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleSwiperRefreshAfterCartMorph);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#initSwipersInDrawer);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#teardownDrawerMorphObserver();
    this.#clearSwiperRefreshTimers();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      await this.closeDialog();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * `cart:update` / CartAdd can land before section morph finishes (especially first item —
   * PDP add often has no `sections` payload and cart-items fetches HTML async).
   */
  #scheduleSwiperRefreshAfterCartMorph = () => {
    this.#queueSwiperRefresh(12);
  };

  #clearSwiperRefreshTimers = () => {
    cancelAnimationFrame(this.#cartDrawerRecSwiperRaf);
    this.#cartDrawerRecSwiperRaf = 0;
    if (this.#cartDrawerRecSwiperTimeout != null) {
      clearTimeout(this.#cartDrawerRecSwiperTimeout);
      this.#cartDrawerRecSwiperTimeout = null;
    }
  };

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  #isRecommendationsMountNode = (node) => {
    if (!(node instanceof HTMLElement)) return false;
    return Boolean(
      node.matches?.('[data-cart-drawer-recommendations], #product-recommendations-cart-drawer') ||
        node.querySelector?.('[data-cart-drawer-recommendations], #product-recommendations-cart-drawer')
    );
  };

  /**
   * @param {MutationRecord[]} mutations
   * @returns {boolean}
   */
  #mutationsMountRecommendations = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (this.#isRecommendationsMountNode(node)) return true;
      }
      for (const node of mutation.removedNodes) {
        if (this.#isRecommendationsMountNode(node)) return true;
      }
    }
    return false;
  };

  #ensureDrawerMorphObserver = () => {
    if (this.#cartDrawerMorphObserver) return;

    const root = this.querySelector('cart-items-component') ?? this;
    this.#cartDrawerMorphObserver = new MutationObserver((mutations) => {
      if (this.#ignoreMorphObserver) return;
      if (!this.refs.dialog?.open) return;
      // Ignore Swiper's own class/DOM churn — only react when recs strip is mounted/replaced.
      if (!this.#mutationsMountRecommendations(mutations)) return;
      this.#queueSwiperRefresh(6);
    });
    this.#cartDrawerMorphObserver.observe(root, { childList: true, subtree: true });
  };

  #teardownDrawerMorphObserver = () => {
    this.#cartDrawerMorphObserver?.disconnect();
    this.#cartDrawerMorphObserver = null;
  };

  /**
   * @param {HTMLElement} swiperEl
   * @returns {boolean}
   */
  #isSwiperReady = (swiperEl) => {
    const themeSwiper = /** @type {Window & { ThemeSwiper?: { getInstance?: (el: HTMLElement) => unknown } }} */ (
      window
    ).ThemeSwiper;
    const instance = themeSwiper?.getInstance?.(swiperEl);
    const width = swiperEl.getBoundingClientRect().width;
    return Boolean(instance && width >= 1 && swiperEl.classList.contains('swiper-initialized'));
  };

  /**
   * @param {number} [retries]
   */
  #queueSwiperRefresh = (retries = 0) => {
    this.#pendingSwiperRetries = Math.max(this.#pendingSwiperRetries, retries);
    if (this.#swiperRefreshQueued) return;
    this.#swiperRefreshQueued = true;

    this.#clearSwiperRefreshTimers();
    this.#cartDrawerRecSwiperRaf = requestAnimationFrame(() => {
      this.#swiperRefreshQueued = false;
      const retriesLeft = this.#pendingSwiperRetries;
      this.#pendingSwiperRetries = 0;

      if (!this.refs.dialog?.open) return;

      this.#ignoreMorphObserver = true;
      this.#refreshCartDrawerSwipers();
      // Keep observer muted until Swiper finishes its sync DOM writes.
      queueMicrotask(() => {
        this.#ignoreMorphObserver = false;
      });

      const swiperEl = this.querySelector('.cart-drawer-recommendations__swiper[data-swiper]');
      if (!(swiperEl instanceof HTMLElement) || !this.#isSwiperReady(swiperEl)) {
        if (retriesLeft > 0) {
          this.#cartDrawerRecSwiperTimeout = setTimeout(() => this.#queueSwiperRefresh(retriesLeft - 1), 50);
        }
        return;
      }

      const themeSwiper = /** @type {Window & { ThemeSwiper?: { getInstance?: (el: HTMLElement) => { update?: () => void } | undefined } }} */ (
        window
      ).ThemeSwiper;
      themeSwiper?.getInstance?.(swiperEl)?.update?.();
    });
  };

  /**
   * Init (or lightly update) recommendations swiper after section morph.
   * Avoid destroy→init when already healthy — that loops with MutationObserver / Swiper observer.
   */
  #refreshCartDrawerSwipers = () => {
    const themeSwiper = /** @type {Window & { ThemeSwiper?: { init: (root?: ParentNode) => void; destroy?: (root?: ParentNode) => void; getInstance?: (el: HTMLElement) => { update?: () => void } | undefined } }} */ (
      window
    ).ThemeSwiper;
    if (typeof themeSwiper?.init !== 'function') return;

    const swiperEl = this.querySelector('.cart-drawer-recommendations__swiper[data-swiper]');
    if (swiperEl instanceof HTMLElement && this.#isSwiperReady(swiperEl)) {
      themeSwiper.getInstance?.(swiperEl)?.update?.();
      return;
    }

    const recHosts = this.querySelectorAll('[data-cart-drawer-recommendations]');
    if (typeof themeSwiper.destroy === 'function') {
      for (const recHost of recHosts) {
        if (recHost instanceof HTMLElement) themeSwiper.destroy(recHost);
      }
    }

    themeSwiper.init(this);

    const nextSwiperEl = this.querySelector('.cart-drawer-recommendations__swiper[data-swiper]');
    if (nextSwiperEl instanceof HTMLElement) {
      themeSwiper.getInstance?.(nextSwiperEl)?.update?.();
    }
  };

  #initSwipersInDrawer = () => {
    this.#ensureDrawerMorphObserver();
    this.#queueSwiperRefresh(12);
  };

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
